<template>
  <div class="table-container" :style="{'min-height' : minHeight + 'px' }">
    <div
      class="table-wrapper"
      :class="{ 'is-scroll': !usePaging }"
      :style="!usePaging ? { '--tbl-h': (minContHeight) + 'px' } : null"
    >
      <div class="table-responsive p-0 flex-grow-1" :class="!usePaging ? 'pb-2' : ''">
        <table class="table align-items-center mb-0" data-tour="dlist.table">
          <colgroup v-if="colgroup && colgroup.length">
            <col v-for="(c, i) in colgroup" :key="i" :style="c" />
          </colgroup>

          <!-- 헤더: 기본은 props.columns, 커스터마이즈 하고 싶으면 slot:thead -->
          <thead>
            <slot name="thead">
              <tr>
                <th v-for="(col, ci) in columns"
                    :key="ci"
                    :class="['text-center', 'text-uppercase', 'text-secondary', 'text-xs', 'font-weight-bolder', col.thClass]">
                    {{ col.label }}
                </th>
              </tr>
            </slot>
          </thead>

          <tbody>
            <!-- 로딩: tbody 중앙 -->
            <tr v-if="loading">
              <td :colspan="columns.length" class="p-0">
                <slot name="loader">
                  <TableBodyLoader :minHeight="minBodyHeight" :message="message"/>
                </slot>
              </td>
            </tr>

            <!-- 데이터 -->
            <template v-else>
              <tr
              v-for="(item, idx) in items"
              :key="rowKey ? rowKey(item, idx) : idx"
              :class="[
                  'data-row',
                  { 'row-hover': rowHover, 'row-clickable': rowClickable }
              ]"
              @click="rowClickable && $emit('row-click', item, idx)"
              >
              <slot name="row" :item="item" :index="idx">
                  <td v-for="(col, ci) in columns" :key="ci" class="text-center text-sm" :class="col.tdClass">
                  {{ col.key ? item[col.key] : '' }}
                  </td>
              </slot>
              </tr>

              <!-- 비어있음 -->
              <tr v-if="items.length === 0 && !loading">
                <td :colspan="columns.length" class="text-center text-muted py-4">
                    <div class="empty-center small" :style="{ 'min-height' : emptyHeight + 'px'}">
                        <slot name="empty">{{ emptyMessage }}</slot>
                    </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- 페이지네이션 하단 고정 -->
      <div class="pagination-wrapper">
        <slot name="pagination">
          <BasePagination
            v-if="usePaging"
            v-model:page="pageProxy"
            v-model:pageSize="pageSizeProxy"
            :page-sizes="pageSizes"
            :total="total"
            :max-length="7"
            align="center"
            :disabled="loading"
          />
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import TableBodyLoader from '@/components/loading/TableBodyLoader.vue'
import BasePagination from '@/components/pagination/BasePagination.vue';

export default {
  name: 'BaseDataTable',
  components: { BasePagination, TableBodyLoader },
  props: {
    loading: { type: Boolean, default: false },
    items:   { type: Array,   default: () => [] },
    columns: { type: Array,   default: () => [] }, // [{ label, key, thClass?, tdClass? }]
    total:   { type: Number,  default: 0 },
    page:    { type: Number,  default: 1 },
    pageSize:{ type: Number,  default: 10 },
    pageSizes:{ type: Array,  default: () => [10, 20, 50] },
    minBodyHeight: { type: Number, default: 500 },
    minContHeight: { type: Number, default: 500 },
    colgroup: { type: Array, default: () => [] },  // 예: ['width:8%','width:12%', ...]
    rowKey: { type: Function }, // (item, index) => uniqueKey
    emptyMessage: { type: String, default: '데이터가 없습니다.' },
    rowHover: { type: Boolean, default: true },
    rowClickable: { type: Boolean, default: true },
    usePaging: { type: Boolean, default: true },
    message: { type: String, default: '불러오는중...' },
    minHeight: { type: Number,  default: 687 },
    emptyHeight: { type: Number,  default: 450 },
  },
  emits: ['update:page', 'update:pageSize', 'row-click'],
  computed: {
    pageProxy: {
      get() { return this.page; },
      set(v) { this.$emit('update:page', v); }
    },
    pageSizeProxy: {
      get() { return this.pageSize; },
      set(v) { this.$emit('update:pageSize', v); }
    }
  }
};
</script>

<style scoped>
.table-container {
  position: relative;
  display: flex;
  flex-direction: column;
}
.table-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}
.table-responsive {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden; 
}
.pagination-wrapper {
  margin-top: auto;
  padding-top: .5rem;
}
.tbody-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

/* progress (Tabler 스타일 발췌) */
.progress {
  display: flex;
  height: 0.5rem;
  overflow: hidden;
  font-size: 0.65625rem;
  background-color: rgba(0, 0, 0, 0.1);
  border-radius: .25rem;
  box-shadow: inset 0 1px 2px rgba(0,0,0,.075);
  position: relative;
  width: 100%;
  line-height: .5rem;
  appearance: none;
}
.progress-sm { height: .25rem; }
.progress-bar {
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  background-color: #206bc4;
  transition: width 0.6s ease;
  height: 100%;
}
@keyframes progress-indeterminate {
  0%   { right: 100%; left: -35%; }
  60%,100% { right: -90%; left: 100%; }
}
.progress-bar-indeterminate::before,
.progress-bar-indeterminate::after {
  position: absolute;
  top: 0; bottom: 0; left: 0;
  content: "";
  background-color: inherit;
  will-change: left, right;
}
.progress-bar-indeterminate::before {
  animation: progress-indeterminate 1.5s cubic-bezier(.65,.815,.735,.395) infinite;
}

.empty-center {
  position: absolute;
  padding-bottom: 40px;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}
/* 각 테이블 로우의 최소 높이 */
.table tbody tr {
  height: 52px;
}
.row-hover:hover {
  background-color: rgba(32, 107, 196, 0.06); /* Tabler primary(206bc4) 톤 참고 */
}

.row-clickable {
  cursor: pointer;
}

/* usePaging=false 일 때 tbody 영역만 스크롤 */
.table-wrapper.is-scroll .table-responsive {
  max-height: 800px;
  min-height: var(--tbl-h, 500px);
  overflow-y: auto;
  overflow-x: auto; /* 컬럼 많을 때 가로 스크롤 허용 */
}

/* 헤더 고정 (sticky) */
.table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--bs-body-bg, #fff);
  /* 아래 보더/섀도우로 헤더 구분감 */
  box-shadow: inset 0 -1px 0 #e9ecef;
}

/* 로딩/빈값 센터링이 스크롤 컨텍스트에서도 정확히 가운데 오도록 */
.tbody-loader {
  min-height: 240px; /* 필요 시 조정 */
  display: flex;
  align-items: center;
  justify-content: center;
}

/* absolute로 중앙잡던 empty는 스크롤 컨테이너와 충돌 가능 → 일반 배치로 */
.empty-center {
  position: static;
  padding: 2rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
}

/* 행 높이/호버는 유지 */
.table tbody tr { height: 52px; }
</style>
